import {
  Component,
  computed,
  inject,
  Input,
  input,
  model,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgSelectModule } from '@ng-select/ng-select';
import { of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from 'rxjs/operators';
import { GeneralAdapter } from '../../adapters/general.adapter';
import { Facility } from '../../interfaces/facility';
import { HereLocation } from '../../interfaces/location';

/**
 * Input component that is attached to a {@link FormControl} and uses the HERE API to pick and select a location.
 */
@UntilDestroy()
@Component({
  selector: 'app-location-input',
  templateUrl: './location-input.component.html',
  styleUrls: ['./location-input.component.scss'],
  imports: [ReactiveFormsModule, NgSelectModule, TranslocoPipe],
})
export class LocationInputComponent implements OnInit {
  @Input() control: FormControl;
  @Input() facility: Facility;
  @Input() placeholder: string;
  /** When true, only complete street addresses (with house number) are offered. */
  readonly requireComplete = input(false);
  /** Optional empty-state text when no matching locations are found. */
  readonly notFoundText = input<string | undefined>(undefined);
  submitAttempt = model<boolean>(false);
  locationsLoading: boolean = false;
  locationsInput$ = new Subject<string>();
  label = model<string>('');
  /** Emits the full selected location, or `null` when the selection is cleared. */
  locationChange = output<HereLocation | null>();

  private readonly searchResults = signal<HereLocation[]>([]);
  private readonly selectedId = signal<string | null>(null);
  readonly displayedLocations = computed(() => {
    let results = this.searchResults();
    if (this.requireComplete()) {
      const hasValidity = results.some(
        (item) => typeof item.valid === 'boolean',
      );
      if (hasValidity) {
        results = results.filter((item) => item.valid);
      }
    }
    const id = this.selectedId();
    const label = this.label();
    if (id && label && !results.some((item) => item.id === id)) {
      return [{ id, label, countryCode: '' }, ...results];
    }
    return results;
  });

  private generalAdapter = inject(GeneralAdapter);

  ngOnInit() {
    this.selectedId.set(this.control.value ?? null);
    this.loadFacilities();

    this.control.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.selectedId.set(result ?? null);
        if (!result) this.label.set('');
      },
    });
  }

  /**
   * Handles a selection change from the underlying select.
   * @param {HereLocation} event Selected location, undefined when cleared
   * @returns {void}
   */
  onChange(event: HereLocation | undefined): void {
    if (this.requireComplete() && event && event.valid === false) {
      this.control.setValue(null);
      this.label.set('');
      this.selectedId.set(null);
      this.locationChange.emit(null);
      return;
    }
    this.label.set(event?.label ?? '');
    this.locationChange.emit(event ?? null);
  }

  private loadFacilities() {
    this.locationsInput$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => (this.locationsLoading = true)),
        switchMap((term) =>
          this.generalAdapter
            .getLocations(term, { completeOnly: this.requireComplete() })
            .pipe(
              catchError(() => of([])),
              tap(() => (this.locationsLoading = false)),
            ),
        ),
        untilDestroyed(this),
      )
      .subscribe({
        next: (results) => {
          this.searchResults.set((results as HereLocation[]) ?? []);
        },
      });
  }

  reset() {
    this.control.reset();
    this.label.set('');
    this.selectedId.set(null);
    this.searchResults.set([]);
    this.submitAttempt.set(false);
    this.locationsInput$.next('');
    this.locationChange.emit(null);
  }
}
